import React, { useEffect, useState } from 'react';
import { MobileShellGate, createCapacitorShell, mobileShellConfig, BiometricLock } from '@platform/mobile-shell';

declare const __PRODUCT_CONFIG__: any;

const shell = createCapacitorShell();
const wiring = shell
  ? mobileShellConfig({
      app: 'rubytill-admin', platform: shell.platform,
      stage: import.meta.env.VITE_STAGE || (import.meta.env.PROD ? 'prod' : 'dev'),
      domain: __PRODUCT_CONFIG__.domain, appId: 'com.rubytill.admin',
    })
  : null;

/** Face ID way out (and any forced sign-out): drop the stored session and reload to the sign-in page. */
function signOutToPassword(): void {
  try {
    for (const k of Object.keys(localStorage)) {
      if (k.startsWith('CognitoIdentityServiceProvider.') || ['jwtToken', 'idToken', 'accessToken', 'refreshToken'].includes(k)) localStorage.removeItem(k);
    }
  } catch { /* storage unavailable */ }
  window.location.reload();
}

function useSignedIn(): boolean {
  const read = () => { try { return !!localStorage.getItem('jwtToken'); } catch { return false; } };
  const [v, setV] = useState<boolean>(read);
  useEffect(() => {
    if (!shell) return;
    const id = setInterval(() => setV(read()), 2000);
    return () => clearInterval(id);
  }, []);
  return v;
}

/**
 * RubyTill Admin phone app; pass-through on the web. Super-admin is
 * ruby-on-light. Update gate → Face ID lock. No push: push-service serves the
 * merchant pool (P5 plan).
 */
export function AdminMobileRoot({ children }: { children: React.ReactNode }) {
  const signedIn = useSignedIn();
  if (!shell || !wiring) return <>{children}</>;
  return (
    <MobileShellGate shell={shell} config={wiring.config} fallbackStoreUrl={wiring.fallbackStoreUrl} theme="light">
      <BiometricLock
        biometric={shell.biometric} store={shell.ports.store} platform={shell.platform} theme="light"
        signedIn={signedIn} onResume={shell.onResume} onPause={shell.onPause} now={shell.ports.now}
        onUsePassword={signOutToPassword}
      >
        {children}
      </BiometricLock>
    </MobileShellGate>
  );
}

/** Open a URL outside the app on a phone; a new tab on the web. */
export function openExternal(url: string): void {
  if (shell) void shell.openUrl(url);
  else window.open(url, '_blank', 'noopener');
}

/** Public web origin: the app is served as its real hostname (capacitor.config server.hostname). */
export const webOrigin = (): string => `https://${window.location.host}`;
